import React, { memo, useEffect, useState } from 'react';
import { isEmpty, isEqual } from 'lodash';
import { Formik } from 'formik';
import { useParams, useLocation } from 'react-router-dom';
import ErrorIcon from '@mui/icons-material/Error';

import Dialog from 'components/ui/Dialog';
import TabBar from 'components/general/TabBar';
import {
  showOrganization,
  updateOrganization,
  createOrganization,
  deleteOrganization,
  uploadOrgLogo,
} from 'api/organization';
import SubRelationSection from 'components/general/SubRelationSection';
import { getBillTo, showBillTo } from 'api/bill-to';
import { formatPhoneNumber } from 'helpers/format-phone-number';
import { getAccountManagers, showAccountManager } from 'api/account-manager';
import { useSnackBar } from 'context/SnackBarContext';
import { revertPhoneMask } from 'helpers/revert-phone-mask';
import { initialValues, validationSchema } from '../organization.config';
import OrganizationDetails from './OrganizationDetails';
import OrganizationSubLocations from './OrganizationSubLocations';
import {
  StickyContainer,
  HeaderSection,
  OrganizationName,
  Address,
  SubRelationHeading,
  SubRelationDetails,
  BillTo,
  LogoWarning,
  LogoWarningText,
} from './styles';
import OrganizationLogo from './OrganizationLogo';

const tabOptions = [
  { label: 'Details', value: 'details' },
  {
    label: 'Sub Locations',
    value: 'sub-locations',
  },
];

const OrganizationModal = memo(
  ({ modalOpen, handleModalClose, blockRouteChange, setBlockRouteChange }) => {
    const [activeTab, setActiveTab] = useState('details');
    const { id } = useParams();
    const { pathname } = useLocation();
    const routeDepth = pathname.split('/').length - 1;

    const [organization, setOrganization] = useState({});
    const [billTos, setBillTos] = useState([]);
    const [billTo, setBillTo] = useState({});
    const [accountManagers, setAccountManagers] = useState([]);
    const [accountManager, setAccountManager] = useState({});
    const [images, setImages] = useState([]);
    const [logoWarning, setLogoWarning] = useState(false);
    const { setMessage, setOpenSnackBar, setSeverity } = useSnackBar();

    const {
      name: orgName,
      address: orgAddress,
      city: orgCity,
      state: orgState,
      postal_code: orgPostalCode,
      country: orgCountry,
      sublocations: subLocations,
      logo,
    } = organization;

    const fetchOrganization = async () => {
      const { data } = await showOrganization(id);

      setOrganization(data.data);
    };

    useEffect(async () => {
      if (id && routeDepth <= 2) {
        fetchOrganization();
      }
    }, []);

    useEffect(async () => {
      const { data: billToData } = await getBillTo(0, 10000);
      const { data: accountManagerData } = await getAccountManagers(0, 10000);

      setBillTos(billToData.data);
      setAccountManagers(accountManagerData.data);
    }, []);

    const handleSave = async (values) => {
      const imageObj = logo || images;

      if (!isEmpty(organization)) {
        try {
          if (!isEmpty(imageObj)) {
            try {
              await uploadOrgLogo(id, images[0].file);
            } catch (error) {
              setMessage('Something went wrong. Failed to upload logo.');
              setSeverity('error');
            }
          } else {
            console.log(3);
            setMessage('Failed to save changes. Logo is required.');
            setSeverity('error');
            setOpenSnackBar(true);
            setLogoWarning(true);
            return;
          }

          await updateOrganization(id, values);
          setMessage('Saved changes');
          setSeverity('success');
        } catch (error) {
          setMessage('Something went wrong. Failed to save changes.');
          setSeverity('error');
        }
      } else {
        try {
          const { data } = await createOrganization(values);
          const { id: newOrgId } = data.data;

          if (!isEmpty(imageObj)) {
            try {
              await uploadOrgLogo(newOrgId, images[0].file);
            } catch (error) {
              await deleteOrganization(newOrgId);
              setMessage('Something went wrong. Failed to upload logo.');
              setSeverity('error');
            }
          }

          setMessage('Organization created');
          setSeverity('success');
        } catch (error) {
          console.error(error);
          setMessage('Something went wrong. Failed to create organization');
          setSeverity('error');
        }
      }

      setOpenSnackBar(true);
      handleModalClose(true);
    };

    const handleDelete = async () => {
      try {
        await deleteOrganization(id);
        setMessage('Organization deleted');
        setSeverity('success');
      } catch (error) {
        console.error(error);
        setMessage('Something went wrong. Failed to delete organization.');
        setSeverity('error');
      }

      setOpenSnackBar(true);
      handleModalClose(true);
    };

    const billToOptionLabel = ({ name }) => name || '';
    const accountManagerOptionLabel = ({
      first_name: firstName,
      last_name: lastName,
    }) => {
      if (firstName || lastName) {
        return `${firstName} ${lastName}`;
      }
      return '';
    };

    const {
      name: billToName,
      address: billToAddress,
      city: billToCity,
      state: billToState,
      postal_code: billToPostalCode,
      country: billToCountry,
      contact_first_name: billToContactFirstName,
      contact_last_name: billToContactLastName,
      contact_title: billToContactTitle,
      contact_email: billToContactEmail,
      contact_phone: billToContactPhone,
    } = billTo;

    const {
      first_name: accountManagerFirstName,
      last_name: accountManagerLastName,
      email: accountManagerEmail,
      rate: accountManagerRate,
    } = accountManager;

    return (
      <>
        <Formik
          initialValues={initialValues(organization)}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={handleSave}
        >
          {({
            values,
            errors,
            setFieldValue,
            handleChange,
            submitForm,
            touched,
          }) => {
            const {
              name,
              shortName,
              contactFirstName,
              contactLastName,
              contactTitle,
              contactEmail,
              contactPhone,
              address,
              city,
              state,
              postalCode,
              country,
              billingId,
              accountManagerId,
            } = values;

            useEffect(async () => {
              if (billingId && billTo.id !== billingId) {
                const data = await showBillTo(billingId);
                setBillTo(data.data);
              }
            }, [billingId]);

            useEffect(async () => {
              if (accountManagerId && accountManager.id !== accountManagerId) {
                const data = await showAccountManager(accountManagerId);
                setAccountManager(data.data);
              }
            }, [accountManagerId]);

            const initValues = initialValues(organization);

            const unsavedChange = !isEqual(
              {
                ...values,
                contactPhone: revertPhoneMask(contactPhone),
                billingId: Number(billingId),
                accountManagerId: Number(accountManagerId),
              },
              {
                ...initValues,
                billingId: Number(initValues.billingId),
                accountManagerId: Number(initValues.accountManagerId),
              },
            );

            setBlockRouteChange(unsavedChange);

            return (
              <Dialog
                open={modalOpen}
                handleClose={handleModalClose}
                submitForm={submitForm}
                handleDelete={handleDelete}
                disableTopPadding
                maxWidth="md"
                typeText="Organization"
                blockRouteChange={blockRouteChange}
              >
                <StickyContainer>
                  {id && routeDepth <= 2 && (
                    <>
                      <HeaderSection>
                        <OrganizationName>{orgName}</OrganizationName>
                        <Address>
                          {orgAddress}, {orgCity}, {orgState} {orgPostalCode},{' '}
                          {orgCountry}
                        </Address>
                      </HeaderSection>

                      <TabBar
                        tabs={tabOptions}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                      />
                    </>
                  )}
                </StickyContainer>

                {activeTab === 'details' ? (
                  <>
                    <OrganizationLogo
                      logo={logo}
                      images={images}
                      setImages={setImages}
                    />

                    {logoWarning && (
                      <LogoWarning>
                        <ErrorIcon fontSize="small" />
                        <LogoWarningText>Logo is required</LogoWarningText>
                      </LogoWarning>
                    )}

                    <OrganizationDetails
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      name={name}
                      shortName={shortName}
                      contactFirstName={contactFirstName}
                      contactLastName={contactLastName}
                      contactTitle={contactTitle}
                      contactEmail={contactEmail}
                      contactPhone={contactPhone}
                      address={address}
                      city={city}
                      state={state}
                      postalCode={postalCode}
                      country={country}
                      setFieldValue={setFieldValue}
                    />

                    <SubRelationSection
                      sectionTitle="Bill To"
                      data={billTos}
                      name="billingId"
                      value={billingId}
                      setFieldValue={setFieldValue}
                      getOptionLabel={billToOptionLabel}
                      groupBy="name"
                      selection={billTo}
                      errors={errors}
                      touched={touched}
                      required
                    >
                      {/* {!isEmpty(billingId) && ( */}
                      <BillTo>
                        <div>
                          <SubRelationHeading>{billToName}</SubRelationHeading>
                          <SubRelationDetails>
                            <div>{billToAddress}</div>
                            <div>
                              {billToCity}, {billToState}, {billToPostalCode}
                            </div>
                            <div>{billToCountry}</div>
                          </SubRelationDetails>
                        </div>

                        <div>
                          <SubRelationHeading>
                            {billToContactFirstName} {billToContactLastName}
                          </SubRelationHeading>
                          <SubRelationDetails>
                            <div>{billToContactTitle}</div>
                            <div>{formatPhoneNumber(billToContactPhone)}</div>
                            <div>{billToContactEmail}</div>
                          </SubRelationDetails>
                        </div>
                      </BillTo>
                      {/* )} */}
                    </SubRelationSection>

                    <SubRelationSection
                      sectionTitle="Account Manager"
                      data={accountManagers}
                      name="accountManagerId"
                      value={accountManagerId}
                      setFieldValue={setFieldValue}
                      getOptionLabel={accountManagerOptionLabel}
                      groupBy="first_name"
                      selection={accountManager}
                      fullName
                      errors={errors}
                      touched={touched}
                    >
                      <div>
                        <SubRelationHeading>
                          {accountManagerFirstName} {accountManagerLastName}
                        </SubRelationHeading>
                        <SubRelationDetails>
                          <div>{accountManagerEmail}</div>
                          <div>Rate - {accountManagerRate}%</div>
                        </SubRelationDetails>
                      </div>
                    </SubRelationSection>
                  </>
                ) : (
                  <OrganizationSubLocations
                    subLocations={subLocations}
                    organizationId={id}
                    fetchOrganization={fetchOrganization}
                  />
                )}
              </Dialog>
            );
          }}
        </Formik>
      </>
    );
  },
);

export default OrganizationModal;
